/**
 * 裁剪记录状态：以 MySQL（crop-images API）中的 status + URL 为准，前端据此决定是否裁剪。
 * 图片文件在服务器目录，库中只存路径 URL 与状态。
 */

import { isManagedStorageUrl, isSignedDisplayUrl } from '@/utils/imageDisplayAccess';
import { pickCroppedDisplayUrlFromRow, pickCroppedUrlFromRow } from './imageCropLoadResults';

/** 详情展示用原图地址：优先可长期签名的 /uploads 受管路径，避免过期 https/签名链当原图 */
export function preferDurableOriginalUrl(primary, fallback = '') {
  const a = String(primary || '').trim();
  const b = String(fallback || '').trim();
  const durable = (url) => Boolean(url && isManagedStorageUrl(url) && !isSignedDisplayUrl(url));
  if (durable(b) && !durable(a)) return b;
  if (durable(a)) return a;
  if (b) return b;
  return a;
}

function readOriginalUrlFromCropData(cropData) {
  return String(
    cropData?.originalImageUrl
    ?? cropData?.original_image_url
    ?? cropData?.originalUrl
    ?? '',
  ).trim();
}

function readOriginalDisplayUrlFromCropData(cropData) {
  return String(
    cropData?.originalImageUrlDisplay
    ?? cropData?.original_image_url_display
    ?? cropData?.originalUrlDisplay
    ?? cropData?.original_display_url
    ?? '',
  ).trim();
}

const TERMINAL_OK = new Set(['success', 'partial']);
const TERMINAL_FAIL = new Set(['failed']);

/** T3-4.1：Qwen bbox pipeline 标识 */
export const IMAGE_CROP_PIPELINE_QWEN_BBOX = 'qwen-bbox-v1';
export const IMAGE_CROP_PIPELINE_QWEN_BBOX_FALLBACK = 'qwen-bbox-fallback-v1';
export const IMAGE_CROP_PIPELINE_CROP_BY_BBOX = 'crop-by-bbox-v1';

const QWEN_BBOX_PIPELINES = new Set([
  IMAGE_CROP_PIPELINE_QWEN_BBOX,
  IMAGE_CROP_PIPELINE_QWEN_BBOX_FALLBACK,
]);

function trimOrEmpty(value) {
  return value == null ? '' : String(value).trim();
}

/** 从库记录读取 pipeline（顶层或 cropPosition 内） */
export function normalizeCropPipeline(row) {
  if (!row || typeof row !== 'object') return '';
  return trimOrEmpty(row.pipeline ?? row.cropPosition?.pipeline);
}

export function isQwenBboxPipeline(pipeline) {
  return QWEN_BBOX_PIPELINES.has(trimOrEmpty(pipeline));
}

function readFiniteNumber(value) {
  if (value == null) return null;
  const num = Number(value);
  return Number.isFinite(num) ? num : null;
}

function readBboxNormalized(raw) {
  const bbox = raw?.bboxNormalized ?? raw?.bbox_normalized;
  if (!Array.isArray(bbox) || bbox.length !== 4) return null;
  const nums = bbox.map((value) => Number(value));
  if (nums.some((value) => !Number.isFinite(value))) return null;
  return nums;
}

/** 规范化 cropPosition（兼容 snake_case 与多印花 prints） */
export function normalizeCropPosition(raw) {
  if (!raw || typeof raw !== 'object') return null;

  const confidence = readFiniteNumber(raw.confidence);
  const label = trimOrEmpty(raw.label) || null;
  const detectError = trimOrEmpty(raw.detectError ?? raw.detect_error) || null;
  const fallbackPipeline = trimOrEmpty(raw.fallbackPipeline ?? raw.fallback_pipeline) || null;
  const prints = normalizeCropPrints(raw.prints);
  const printCount = readFiniteNumber(raw.printCount ?? raw.print_count) ?? (prints.length || null);

  return {
    bboxNormalized: readBboxNormalized(raw),
    confidence,
    label,
    detectError,
    manual: Boolean(raw.manual),
    sourceWidth: readFiniteNumber(raw.sourceWidth ?? raw.source_width),
    sourceHeight: readFiniteNumber(raw.sourceHeight ?? raw.source_height),
    fallbackPipeline,
    multiPrint: Boolean(raw.multiPrint ?? raw.multi_print) || (prints.length > 1),
    printCount,
    prints,
  };
}

function normalizeCropPrints(rawPrints) {
  if (!Array.isArray(rawPrints)) return [];
  return rawPrints
    .map((item, index) => normalizeCropPrintItem(item, index))
    .filter(Boolean);
}

function normalizeCropPrintItem(raw, fallbackIndex = 0) {
  if (!raw || typeof raw !== 'object') return null;
  const bboxNormalized = readBboxNormalized(raw);
  const confidence = readFiniteNumber(raw.confidence);
  const label = trimOrEmpty(raw.label) || null;
  const printIndex = readFiniteNumber(raw.printIndex ?? raw.print_index) ?? fallbackIndex;
  const croppedImageUrl = trimOrEmpty(
    raw.croppedImageUrl ?? raw.cropped_image_url ?? raw.croppedUrl ?? '',
  ) || null;
  const croppedImageUrlDisplay = trimOrEmpty(
    raw.croppedImageUrlDisplay ?? raw.cropped_image_url_display ?? raw.croppedDisplayUrl ?? '',
  ) || null;
  if (!bboxNormalized && confidence == null && !label && !croppedImageUrl) {
    return null;
  }
  return {
    printIndex,
    bboxNormalized,
    confidence,
    label,
    croppedImageUrl,
    croppedImageUrlDisplay,
    width: readFiniteNumber(raw.width),
    height: readFiniteNumber(raw.height),
  };
}

export function resolveCropUsedFallback(row, pipeline = '') {
  if (Boolean(row?.usedFallback)) return true;
  return trimOrEmpty(pipeline) === IMAGE_CROP_PIPELINE_QWEN_BBOX_FALLBACK;
}

/** 供详情页 InfoCell 展示的 cropPosition 摘要 */
export function buildCropPositionDisplay(cropPosition, { pipeline = '', usedFallback = false } = {}) {
  const normalized = normalizeCropPosition(cropPosition);
  if (!normalized) {
    return {
      hasDetectResult: false,
      label: null,
      confidence: null,
      confidenceText: null,
      detectError: null,
      bboxNormalized: null,
      pipeline,
      pipelineLabel: resolveCropPipelineLabel(pipeline),
      usedFallback: Boolean(usedFallback),
      isQwenBbox: isQwenBboxPipeline(pipeline),
    };
  }

  const confidence = normalized.confidence;
  const confidenceText = confidence == null
    ? null
    : `${Math.max(0, Math.min(100, Math.round(confidence * 100)))}%`;

  return {
    hasDetectResult: Boolean(
      normalized.label
      || confidence != null
      || normalized.bboxNormalized
      || normalized.detectError,
    ),
    label: normalized.label,
    confidence,
    confidenceText,
    detectError: normalized.detectError,
    bboxNormalized: normalized.bboxNormalized,
    manual: normalized.manual,
    pipeline,
    pipelineLabel: resolveCropPipelineLabel(pipeline),
    usedFallback: Boolean(usedFallback),
    isQwenBbox: isQwenBboxPipeline(pipeline),
  };
}

export function resolveCropPipelineLabel(pipeline) {
  const value = trimOrEmpty(pipeline);
  if (!value) return null;
  if (value === IMAGE_CROP_PIPELINE_QWEN_BBOX) return 'Qwen 检测';
  if (value === IMAGE_CROP_PIPELINE_QWEN_BBOX_FALLBACK) return 'Qwen 检测（本地 fallback）';
  if (value === IMAGE_CROP_PIPELINE_CROP_BY_BBOX) return '手动框选';
  return value;
}

/** 从库记录解析规范状态 */
export function normalizeCropRowStatus(row) {
  if (!row) return 'pending';
  const raw = String(row.status ?? row.crop_status ?? '').toLowerCase().trim();
  if (TERMINAL_OK.has(raw)) return pickCroppedUrlFromRow(row) ? raw : 'pending';
  if (TERMINAL_FAIL.has(raw)) return 'failed';
  if (raw === 'processing') return 'processing';
  if (pickCroppedUrlFromRow(row)) return 'success';
  return 'pending';
}

export function isCropRowSuccess(row) {
  const st = normalizeCropRowStatus(row);
  return st === 'success' || st === 'partial';
}

export function isCropRowFailed(row) {
  return normalizeCropRowStatus(row) === 'failed';
}

export function isCropRowPending(row) {
  const st = normalizeCropRowStatus(row);
  return st === 'pending' || st === 'processing';
}

export function isTerminalCropTaskStatus(status) {
  const s = String(status || '').toLowerCase();
  return ['success', 'partial', 'failed', 'canceled', 'cancelled'].includes(s);
}

/** 是否需要在本次批次中执行裁剪（非强制时：已成功/已失败则跳过） */
export function shouldProcessImageCrop(row, { forceReprocess = false } = {}) {
  if (forceReprocess) return true;
  if (!row) return true;
  const st = normalizeCropRowStatus(row);
  if (TERMINAL_OK.has(st)) return false;
  if (TERMINAL_FAIL.has(st)) return false;
  return true;
}

/** 将库记录映射为详情页一行展示数据 */
export function mapCropRowToDetailRow(imageId, cropData, originalUrl = '') {
  const croppedUrl = pickCroppedUrlFromRow(cropData);
  const croppedDisplayUrl = pickCroppedDisplayUrlFromRow(cropData) || croppedUrl;
  const dbStatus = normalizeCropRowStatus(cropData);
  let status = 'pending';
  if (dbStatus === 'success' || dbStatus === 'partial') status = 'success';
  else if (dbStatus === 'failed') status = 'failed';
  else if (dbStatus === 'processing') status = 'processing';

  const origFromDb = readOriginalUrlFromCropData(cropData);
  const originalDisplayUrl = readOriginalDisplayUrlFromCropData(cropData);
  const pipeline = normalizeCropPipeline(cropData);
  const cropPosition = normalizeCropPosition(cropData?.cropPosition);
  const usedFallback = resolveCropUsedFallback(cropData, pipeline);
  const cropPositionDisplay = buildCropPositionDisplay(cropPosition, { pipeline, usedFallback });

  return {
    imageId,
    // 任务 result.referenceUrls（受管）优先于库里的临时外链，避免线上原图裂图
    originalUrl: preferDurableOriginalUrl(origFromDb, originalUrl),
    originalDisplayUrl,
    croppedUrl,
    croppedDisplayUrl,
    status,
    error: cropData?.error || cropData?.message || null,
    pipeline,
    isQwenBbox: isQwenBboxPipeline(pipeline),
    cropPosition,
    cropPositionDisplay,
    usedFallback,
  };
}

/** 构建写入 MySQL 的 payload（裁剪开始 / 成功 / 失败） */
export function buildCropImageSavePayload({
  imageId,
  originalImageUrl = '',
  croppedImageUrl = '',
  width = 0,
  height = 0,
  status = 'processing',
  error = null,
}) {
  return {
    imageId: String(imageId),
    originalImageUrl: String(originalImageUrl || '').trim(),
    croppedImageUrl: String(croppedImageUrl || '').trim(),
    width: width || undefined,
    height: height || undefined,
    status,
    ...(error ? { error: String(error) } : {}),
  };
}
